import type { Span } from '@opentelemetry/api';
import type { CreateSessionResponse, SaveStateResponse, SelfServiceState } from '../../types';
import type { SelfServiceApiContext } from './types';
import { runWithSpan } from './tracing';

const ensureOk = async (response: Response, errorMessage: string): Promise<Response> => {
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`${errorMessage}: ${response.status} ${errorText}`);
  }
  return response;
};

const setSessionAttribute = (span: Span, sessionId: string | undefined): void => {
  if (sessionId) {
    span.setAttribute('session.id', sessionId);
  }
};

export const createSession = async (ctx: SelfServiceApiContext): Promise<CreateSessionResponse> => {
  return runWithSpan('api.createSession', async span => {
    await ctx.delay(ctx.config.delays.createSession);

    const response = await ctx.httpClient.postJSON(`${ctx.baseUrl}/selfservice/session`, { serviceType: 'passport' }, {
      maxRetries: 3,
      onRetry: (attempt: number) => console.log(`Retrying createSession... attempt ${attempt}`)
    });

    await ensureOk(response, 'Create session failed with status');
    const data = await response.json();
    setSessionAttribute(span, data.sessionId);
    return { sessionId: data.sessionId };
  });
};

export const saveState = async (ctx: SelfServiceApiContext, sessionId: string, state: SelfServiceState): Promise<SaveStateResponse> => {
  return runWithSpan('api.saveState', async span => {
    setSessionAttribute(span, sessionId);
    await ctx.delay(ctx.config.delays.saveState);

    const response = await ctx.httpClient.fetchWithRetry(
      `${ctx.baseUrl}/selfservice/${sessionId}/state`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(state)
      },
      {
        maxRetries: 5,
        retryDelay: 500,
        onRetry: (attempt: number, error: any) => console.log(`Retrying saveState... attempt ${attempt}:`, error?.message ?? error)
      }
    );

    await ensureOk(response, 'Failed to save session state');
    const data = await response.json();
    return { ok: Boolean(data.success) };
  });
};

export const getState = async (ctx: SelfServiceApiContext, sessionId: string): Promise<SelfServiceState> => {
  return runWithSpan('api.getState', async span => {
    setSessionAttribute(span, sessionId);
    await ctx.delay(ctx.config.delays.getState);

    const response = await ctx.httpClient.getJSON(`${ctx.baseUrl}/selfservice/${sessionId}/state`, {
      maxRetries: 3,
      onRetry: (attempt: number) => console.log(`Retrying getState... attempt ${attempt}`)
    });

    await ensureOk(response, 'Failed to load session state');
    return response.json();
  });
};

export const saveBeforeRedirect = async (ctx: SelfServiceApiContext, sessionId: string, state: SelfServiceState, redirectType: string): Promise<SaveStateResponse> => {
  const redirectState: SelfServiceState = {
    ...state,
    redirectType,
    redirectTimestamp: Date.now()
  };

  return saveState(ctx, sessionId, redirectState);
};


